﻿import React, {useState} from 'react';
import {
    Typography,
    Box,
    Link,
    Chip,
    TextField,
    Button,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {sendUserNotFoundForm} from './sendUserNotFoundForm';
import {Translate} from '../../shared/LanguageService';
import {useTelemetryContext} from "../../telemetry/telemetryContext";
import SuccessSnackbar from '../../shared/feedback/SuccessSnackbar';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';
import landingPage from '../../assets/images/landingpage.png';

enum FormFieldNames {
    Name = 'name',
    BrokerFirm = 'brokerFirm',
    Email = 'email',
    PhoneNumber = 'phoneNumber',
    OrganizationNumber = 'organizationNumber',
    Message = 'message'
}

function UserNotFoundAccessPage() {
    const [sentEmail, setSentEmail] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    let { logger } = useTelemetryContext();
    
    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        
        let formData = new FormData(event.currentTarget);
        let name = formData.get(FormFieldNames.Name) as string;
        let brokerFirm = formData.get(FormFieldNames.BrokerFirm) as string;
        let email = formData.get(FormFieldNames.Email) as string;
        let phoneNumber = formData.get(FormFieldNames.PhoneNumber) as string;
        let organizationNumber = formData.get(FormFieldNames.OrganizationNumber) as string;
        let message = formData.get(FormFieldNames.Message) as string;

        const errors: Record<string, string> = {};

        if (!name.trim()) {
            errors[FormFieldNames.Name] = Translate('user-not-found-form/errors/name-missing');
        }
        if (!brokerFirm.trim()) {
            errors[FormFieldNames.BrokerFirm] = Translate('user-not-found-form/errors/brokerFirm-missing');
        }
        if (!email.trim()) {
            errors[FormFieldNames.Email] = Translate('user-not-found-form/errors/email-missing');
        } else {
            const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
            if (!emailRegex.test(email)) {
                errors[FormFieldNames.Email] = Translate('error-pages/user-not-found/invalid-email');
            }
        }
        if (!phoneNumber.trim()) {
            errors[FormFieldNames.PhoneNumber] = Translate('user-not-found-form/errors/phone-missing');
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setFieldErrors({});
            
        try {
          await sendUserNotFoundForm(
                name,
                brokerFirm,
                email,
                phoneNumber,
                organizationNumber,
                message);
            setSentEmail(true);
            setShowSuccess(true);
        } catch (error) {
            logger.logException(error, {
                origin: 'UserNotFoundAccessPage.sendUserNotFoundForm',
                formData: {
                    name,
                    brokerFirm,
                    email,
                    phoneNumber,
                    organizationNumber,
                    message
                }
            });
            setShowError(true);
        }
    }

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                paddingTop: '96px'
            }}
        >
            <Box sx={{display: 'flex', height: '100%', width: '100vw'}}>
                <Box
                    sx={{
                        zIndex: 2,
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        paddingLeft: {xs: '1rem', md: '2.5rem'},
                        paddingRight: {xs: '1rem', md: 0},
                        backgroundColor: '#DEE3E2',
                        width: {xs: '100%', md: 580},
                        minWidth: {xs: 'auto', md: 580},
                        height: '100%',
                        boxShadow: 12,
                    }}
                >
                    <Box
                        sx={{
                            height: '80%',
                            display: 'flex',
                            flexDirection: 'column',
                            width: {xs: '100%', md: 508},
                            maxWidth: 508,
                        }}
                    >
                        <Box
                            sx={{
                                paddingBlock: '1.5rem',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                width: '100%',
                            }}
                        >
                            <Typography
                                variant="h1"
                                sx={{
                                    fontFamily: 'gilroy-bold, Arial, sans-serif',
                                    fontSize: '2.5rem',
                                    lineHeight: 1.2,
                                    fontWeight: 700,
                                }}
                            >
                                {Translate('error-pages/user-not-found/title')}
                            </Typography>
                        </Box>

                        {sentEmail ? (
                            <Box sx={{marginBlock: '3rem'}}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontFamily: 'gilroy-bold, Arial, sans-serif',
                                        fontSize: '1.9rem',
                                        lineHeight: 1.5,
                                        fontWeight: 600,
                                    }}
                                >
                                    {Translate('error-pages/user-not-found/thank-you')}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontFamily: 'Gilroy-Regular, Arial, sans-serif',
                                        fontSize: '1.5rem',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {Translate('error-pages/user-not-found/confirmation-message')}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Box component="section" sx={{marginBlock: '1.5rem'}}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontFamily: 'Gilroy-Regular, Arial, sans-serif',
                                            fontSize: '1.5rem',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {Translate('error-pages/user-not-found/message')}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontFamily: 'Gilroy-Regular, Arial, sans-serif',
                                            fontSize: '1.5rem',
                                            lineHeight: 1.5,
                                            marginTop: '1rem',
                                            fontWeight:600
                                        }}
                                    >
                                        {Translate('error-pages/user-not-found/contact-support')}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        gap: '15px',
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontFamily: 'gilroy-bold, Arial, sans-serif',
                                            fontSize: '1.5rem',
                                            lineHeight: 1.5,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {Translate('error-pages/user-not-found/request-access')}
                                    </Typography>

                                    <Box
                                        component="form"
                                        noValidate
                                        onSubmit={handleSubmit}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%',
                                            gap: '15px',
                                        }}
                                    >
                                        <TextField
                                            required
                                            name={FormFieldNames.Name}
                                            label={Translate('error-pages/user-not-found/name')}
                                            error={!!fieldErrors[FormFieldNames.Name]}
                                            helperText={fieldErrors[FormFieldNames.Name]}
                                        />
                                        <TextField
                                            required
                                            name={FormFieldNames.BrokerFirm}
                                            label={Translate('error-pages/user-not-found/brokerfirm')}
                                            error={!!fieldErrors[FormFieldNames.BrokerFirm]}
                                            helperText={fieldErrors[FormFieldNames.BrokerFirm]}
                                        />
                                        <TextField
                                            required
                                            name={FormFieldNames.Email}
                                            type="email"
                                            label={Translate('error-pages/user-not-found/email')}
                                            error={!!fieldErrors[FormFieldNames.Email]}
                                            helperText={fieldErrors[FormFieldNames.Email]}
                                        />
                                        <TextField
                                            required
                                            name={FormFieldNames.PhoneNumber}
                                            type="tel"
                                            label={Translate('error-pages/user-not-found/phone-number')}
                                            error={!!fieldErrors[FormFieldNames.PhoneNumber]}
                                            helperText={fieldErrors[FormFieldNames.PhoneNumber]}
                                        />
                                        <TextField
                                            name={FormFieldNames.OrganizationNumber}
                                            label={Translate('error-pages/user-not-found/organizationnumber')}
                                        />
                                        <TextField
                                            name={FormFieldNames.Message}
                                            label={Translate('error-pages/user-not-found/message-label')}
                                            multiline
                                            rows={5}
                                        />
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            sx={{
                                                backgroundColor: '#333D45',
                                                width: 99,
                                                height: 42,
                                                marginBottom: '1rem',
                                                alignSelf: 'flex-end',
                                                letterSpacing: '0.46px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {Translate('error-pages/user-not-found/send')}
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: {xs: 'none', md: 'block'},
                        zIndex: 0,
                        backgroundImage: `url(${landingPage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        flexGrow: 1,
                        height: '100%',
                    }}
                />
            </Box>
            <SuccessSnackbar
                open={showSuccess}
                onClose={() => setShowSuccess(false)}
                text={Translate('error-pages/user-not-found/send-success')}
            />
            <FailureSnackbar
                open={showError}
                onClose={() => setShowError(false)}
                text={Translate('error-pages/user-not-found/send-error')}
            />
        </Box>
    );
}

export default UserNotFoundAccessPage;
